import { useCallback, useEffect, useState } from 'react';
// Shared light/dark switch for the WHOLE platform. The choice is persisted in a
// cookie scoped to the registrable parent domain (`.retink.test` in dev,
// `.retink.ai` in prod) so EVERY subdomain surface reads the same value: set
// dark on app.retink.test and console / flows / filesystem / sqldb / knowledge
// all come up dark too. (A prior version stored it in localStorage, which is
// per-origin and so did NOT carry across the tenant plane despite the comment
// claiming it did — that's the bug this fixes.) Toggling `.dark` on <html> flips
// the semantic surface tokens in web-shared/theme.css; web-ui primitives read
// those tokens, so one switch re-skins every surface — no per-component `dark:`.
const KEY = 'retink.theme';
// `.retink.test` / `.retink.ai` derived from the current host so the cookie spans
// every sibling subdomain. Bare `localhost` / a raw IP (single-app dev without
// Caddy) has no shared parent to scope to, so the cookie falls back to host-only
// — per-origin, exactly the old behaviour, no worse off.
function cookieDomain() {
    if (typeof window === 'undefined')
        return null;
    const host = window.location.hostname;
    if (host === 'localhost' || /^[\d.]+$/.test(host))
        return null;
    const parts = host.split('.');
    if (parts.length < 2)
        return null;
    return '.' + parts.slice(-2).join('.');
}
function readCookie() {
    if (typeof document === 'undefined')
        return null;
    const m = document.cookie.match(/(?:^|;\s*)retink\.theme=([^;]*)/);
    const v = m?.[1] ? decodeURIComponent(m[1]) : null;
    return v === 'light' || v === 'dark' ? v : null;
}
function writeCookie(scheme) {
    if (typeof document === 'undefined')
        return;
    const domain = cookieDomain();
    // A UI preference, not a credential: deliberately readable by JS (no HttpOnly),
    // SameSite=Lax (subdomains of one registrable domain are same-site), 1-year
    // life. Secure only under https so plain-HTTP dev still persists.
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
        `${KEY}=${scheme}; Path=/; Max-Age=31536000; SameSite=Lax` +
            (domain ? `; Domain=${domain}` : '') +
            secure;
}
function readInitial() {
    if (typeof window === 'undefined')
        return 'light';
    // cookie (cross-host) -> legacy localStorage (one-time migration of an existing
    // per-origin choice) -> OS preference.
    const cookie = readCookie();
    if (cookie)
        return cookie;
    const stored = window.localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark')
        return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
function applyScheme(scheme) {
    if (typeof document === 'undefined')
        return;
    document.documentElement.classList.toggle('dark', scheme === 'dark');
}
function persist(scheme) {
    writeCookie(scheme);
    // Same-origin mirror — a fast local cache and a fallback if cookies are blocked.
    try {
        window.localStorage.setItem(KEY, scheme);
    }
    catch { /* storage blocked */ }
}
// Apply the persisted scheme at module load — before React first paints — so a
// surface that should be dark doesn't flash light while its bundle boots after a
// cross-host hop. Idempotent with the hook's effect below; no-op outside a DOM.
applyScheme(readInitial());
// Mount ONE instance per app (ShellLayout owns it on shell surfaces; standalone
// surfaces like the login can mount <ThemeToggle/>, which calls this itself).
export function useColorScheme() {
    const [scheme, setSchemeState] = useState(readInitial);
    useEffect(() => {
        applyScheme(scheme);
        persist(scheme);
    }, [scheme]);
    const setScheme = useCallback((s) => setSchemeState(s), []);
    const toggle = useCallback(() => setSchemeState((s) => (s === 'dark' ? 'light' : 'dark')), []);
    return { scheme, setScheme, toggle };
}
